import React, { useEffect, useState } from 'react';
import { WEBApi } from '../API/WEBApi';
import { Link } from 'react-router-dom';
import { Pagination, Modal } from 'react-bootstrap';

interface ImageUpload {
  _id: string;
  filename: string;
  url: string;
  uploadedAt: string;
  size?: number; // in bytes
}

const UploadedImages: React.FC = () => {
  const [images, setImages] = useState<ImageUpload[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<ImageUpload | null>(null);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const res = await WEBApi.getUploadedImages(page);
      setImages(res.images || []);
      setTotalPages(res.totalPages || 1);
    } catch (err) {
      console.error('Error fetching uploaded images:', err);
      setError('Failed to load images.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, [page]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    setDeletingId(id);
    try {
      await WEBApi.deleteImage(id);
      fetchImages();
    } catch (err) {
      console.error('Error deleting image:', err);
      alert('Failed to delete image.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleImageClick = (img: ImageUpload) => {
    setSelectedImage(img);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedImage(null);
  };

  const formatSize = (bytes?: number): string => {
    if (!bytes) return '';
    const kb = bytes / 1024;
    const mb = kb / 1024;
    return mb >= 1 ? `${mb.toFixed(2)} MB` : `${kb.toFixed(1)} KB`;
  };

  const renderPagination = () => {
    let items = [];
    for (let number = 1; number <= totalPages; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === page}
          onClick={() => setPage(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="justify-content-end mt-4">
        <Pagination.Prev onClick={() => setPage(page - 1)} disabled={page === 1} />
        {items}
        <Pagination.Next onClick={() => setPage(page + 1)} disabled={page === totalPages} />
      </Pagination>
    );
  };

  return (
    <div className="container mt-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Uploaded Images</h3>
        <Link to="/breathe-ev-admin/uploadedImages/create" className="btn btn-success">
          + Create
        </Link>
      </div>

      {loading && (
        <div className="text-center my-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      {error && <div className="alert alert-danger">{error}</div>}

      {!loading && !error && (
        <>
          {images.length === 0 ? (
            <p>No images found.</p>
          ) : (
            <div className="row">
              {images.map((img) => (
                <div key={img._id} className="col-md-3 mb-4">
                  <div className="card h-100">
                    <img
                      src={img.url}
                      alt={img.filename}
                      className="card-img-top"
                      style={{ maxHeight: '200px', objectFit: 'cover', cursor: 'pointer' }}
                      onClick={() => handleImageClick(img)}
                    />
                    <div className="card-body d-flex flex-column justify-content-between">
                      <div>
                        <p className="card-text small mb-1">{img.filename}</p>
                        <p className="text-muted small mb-1">
                          {new Date(img.uploadedAt).toLocaleDateString()}
                        </p>
                        <p className="text-muted small mb-0">
                          Size: {formatSize(img.size)}
                        </p>
                      </div>
                      <button
                        className="btn btn-sm btn-danger mt-2"
                        onClick={() => handleDelete(img._id)}
                        disabled={deletingId === img._id}
                      >
                        {deletingId === img._id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {renderPagination()}
        </>
      )}

      {/* Modal for full-size image */}
      {selectedImage && (
        <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{selectedImage.filename}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            <img
              src={selectedImage.url}
              alt={selectedImage.filename}
              style={{ maxWidth: '100%', maxHeight: '80vh' }}
            />
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
};

export default UploadedImages;
